import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Upload, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle, 
  RefreshCw, Trash2, UserCheck, History, FileWarning,
  ArrowRight, ArrowLeft, Loader2, MapPin, Eye, Sparkles, PartyPopper,
  Calendar as CalendarIcon
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const REQUIRED_COLUMNS = [
  'Policy_Number', 'Insured_Name', 'Agent_Name',
  'Annual_Premium', 'Status'
];
const OPTIONAL_COLUMNS = ['Submitted_Date', 'Issued_Date'];

const ATLAS_FIELD_LABELS = {
  Policy_Number: 'Policy Number',
  Insured_Name: 'Insured Name',
  Agent_Name: 'Agent Name',
  Annual_Premium: 'Annual Premium',
  Status: 'Status',
  Submitted_Date: 'Submitted Date',
  Issued_Date: 'Issued Date',
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Generate selectable years: 5 past + current + 1 future
const _currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 7 }, (_, i) => String(_currentYear - 5 + i));

const WIZARD_STEPS = [
  { id: 1, label: 'Upload', icon: Upload },
  { id: 2, label: 'Map Columns', icon: MapPin },
  { id: 3, label: 'Preview', icon: Eye },
  { id: 4, label: 'Processing', icon: Sparkles },
  { id: 5, label: 'Summary', icon: PartyPopper },
];

const NONE_SENTINEL = '__none__';  // Select.Item disallows empty-string value

export default function CSVImport() {
  const { getAuthHeader } = useAuth();
  const [carriers, setCarriers] = useState([]);
  const [activeTab, setActiveTab] = useState('import');
  const location = useLocation();
  // Honor ?tab=exceptions (used by the Dashboard "unmatched policies" banner).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && ['import', 'exceptions', 'history'].includes(tab)) {
      setActiveTab(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const [exceptions, setExceptions] = useState([]);
  const [exceptionsLoading, setExceptionsLoading] = useState(false);
  const [importHistory, setImportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgentForRetry, setSelectedAgentForRetry] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null); // session being deleted
  const [deletingImport, setDeletingImport] = useState(false);

  // ───────── Import Wizard State ─────────
  const [wizardStep, setWizardStep] = useState(1); // 1..5
  // Step 1: Upload + metadata
  const [selectedCarrier, setSelectedCarrier] = useState('');
  const [payDate, setPayDate] = useState('');
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  // Step 2: Parsed headers + user mapping
  const [parsedHeaders, setParsedHeaders] = useState(null); // { pending_session_id, csv_columns, sample_rows, suggested_mapping, ... }
  const [columnMapping, setColumnMapping] = useState({}); // atlas_field -> csv_column
  // Step 3: Validation result preview
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  // Step 4 & 5: Confirm/Summary
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Fetch carriers on mount
  useEffect(() => {
    fetchCarriers();
    fetchAgents();
  }, []);

  const fetchCarriers = async () => {
    try {
      const res = await axios.get(`${API}/api/csv-import/carriers`, getAuthHeader());
      setCarriers(res.data.carriers || []);
    } catch (err) {
      toast.error('Failed to load carriers');
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/users`, getAuthHeader());
      setAgents(res.data || []);
    } catch (err) {
      console.error('Failed to load agents');
    }
  };

  const fetchExceptions = useCallback(async () => {
    setExceptionsLoading(true);
    try {
      const res = await axios.get(`${API}/api/csv-import/exceptions?resolved=false&limit=100`, getAuthHeader());
      setExceptions(res.data.exceptions || []);
    } catch (err) {
      toast.error('Failed to load exceptions');
    } finally {
      setExceptionsLoading(false);
    }
  }, [getAuthHeader]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/api/csv-import/history?limit=20`, getAuthHeader());
      setImportHistory(res.data.imports || []);
    } catch (err) {
      toast.error('Failed to load import history');
    } finally {
      setHistoryLoading(false);
    }
  }, [getAuthHeader]);

  const confirmDeleteImport = async () => {
    if (!deleteTarget) return;
    setDeletingImport(true);
    try {
      const res = await axios.delete(
        `${API}/api/csv-import/sessions/${deleteTarget.id}`,
        getAuthHeader()
      );
      toast.success(res.data.message || 'Import reversed');
      setDeleteTarget(null);
      await fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete import');
    } finally {
      setDeletingImport(false);
    }
  };

  // Purge-all confirmation + request state
  const [purgeConfirm, setPurgeConfirm] = useState(false);
  const [purgeTyped, setPurgeTyped] = useState('');
  const [purging, setPurging] = useState(false);

  const confirmPurgeAll = async () => {
    if (purgeTyped !== 'PURGE') {
      toast.error('Type PURGE to confirm');
      return;
    }
    setPurging(true);
    try {
      const res = await axios.delete(
        `${API}/api/admin/production/purge-all?confirm=PURGE`,
        getAuthHeader()
      );
      toast.success(res.data.message || 'All production data purged');
      setPurgeConfirm(false);
      setPurgeTyped('');
      await fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to purge production data');
    } finally {
      setPurging(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'exceptions') {
      fetchExceptions();
    } else if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, fetchExceptions, fetchHistory]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setParsedHeaders(null);
      setColumnMapping({});
      setValidationResult(null);
      setImportResult(null);
    }
  };

  const resetWizard = () => {
    setWizardStep(1);
    setSelectedCarrier('');
    setPayDate('');
    setFile(null);
    setParsedHeaders(null);
    setColumnMapping({});
    setValidationResult(null);
    setImportResult(null);
    const fileInput = document.getElementById('csv-file-input');
    if (fileInput) fileInput.value = '';
  };

  // Step 1 → 2: parse headers
  const handleParseHeaders = async () => {
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }
    if (!selectedCarrier) {
      toast.error('Please select a carrier');
      return;
    }

    setParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API}/api/csv-import/parse-headers`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      setParsedHeaders(res.data);
      setColumnMapping({ ...(res.data.suggested_mapping || {}) });
      setWizardStep(2);
      const mappedCount = Object.keys(res.data.suggested_mapping || {}).length;
      toast.success(`Detected ${res.data.csv_columns.length} columns — ${mappedCount} auto-mapped. Review & adjust.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse CSV headers');
    } finally {
      setParsing(false);
    }
  };

  // Step 2 → 3: validate with user mapping
  const handleValidateMapping = async () => {
    // Verify all required fields are mapped
    const unmapped = REQUIRED_COLUMNS.filter(f => !columnMapping[f]);
    if (unmapped.length > 0) {
      toast.error(`Map required fields: ${unmapped.map(f => ATLAS_FIELD_LABELS[f]).join(', ')}`);
      return;
    }
    // Verify no duplicate CSV column assignments
    const assigned = REQUIRED_COLUMNS.concat(OPTIONAL_COLUMNS)
      .map(f => columnMapping[f])
      .filter(Boolean);
    if (new Set(assigned).size !== assigned.length) {
      toast.error('Each CSV column can only be mapped to one Atlas field');
      return;
    }

    setValidating(true);
    try {
      const res = await axios.post(`${API}/api/csv-import/validate-mapped`, {
        pending_session_id: parsedHeaders.pending_session_id,
        carrier: selectedCarrier,
        column_mapping: columnMapping,
        pay_date: payDate,
      }, getAuthHeader());

      setValidationResult(res.data);
      setWizardStep(3);
      if (res.data.matched_rows === 0) {
        toast.warning('No matching agents found. Check the Agent_Name mapping.');
      } else {
        toast.success(
          `Ready to import: ${res.data.new_rows_count ?? res.data.matched_rows} new, ${res.data.update_rows_count ?? 0} updates, ${res.data.exception_rows} exceptions.`
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  // Step 3 → 4 → 5: execute the import
  const handleConfirmImport = async () => {
    if (!validationResult?.session_id) {
      toast.error('No valid session. Please re-validate.');
      return;
    }
    setImporting(true);
    setWizardStep(4);
    try {
      const res = await axios.post(`${API}/api/csv-import/confirm`, {
        carrier: selectedCarrier,
        session_id: validationResult.session_id,
      }, getAuthHeader());
      setImportResult(res.data);
      setWizardStep(5);
      toast.success(
        `Import complete! ${res.data.records_inserted} new, ${res.data.records_updated || 0} updated.`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
      setWizardStep(3); // back to preview
    } finally {
      setImporting(false);
    }
  };

  const handleResolveException = async (exceptionId) => {
    try {
      await axios.put(`${API}/api/csv-import/exceptions/${exceptionId}/resolve`, {}, getAuthHeader());
      toast.success('Exception marked as resolved');
      fetchExceptions();
    } catch (err) {
      toast.error('Failed to resolve exception');
    }
  };

  const handleDeleteException = async (exceptionId) => {
    if (!window.confirm('Are you sure you want to delete this exception?')) return;
    
    try {
      await axios.delete(`${API}/api/csv-import/exceptions/${exceptionId}`, getAuthHeader());
      toast.success('Exception deleted');
      fetchExceptions();
    } catch (err) {
      toast.error('Failed to delete exception');
    }
  };

  const handleRetryException = async (exceptionId) => {
    const agentId = selectedAgentForRetry[exceptionId];
    if (!agentId) {
      toast.error('Please select an agent');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('agent_id', agentId);
      
      await axios.post(`${API}/api/csv-import/exceptions/${exceptionId}/retry`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Policy assigned successfully');
      fetchExceptions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign policy');
    }
  };

  // Format any ISO date (yyyy-MM-dd) or full ISO timestamp as US MM/DD/YYYY.
  // Keeps back-compat for legacy MM/DD/YYYY strings by letting Date parse them.
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      // Pure date (yyyy-MM-dd) — anchor to midnight to avoid TZ drift
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const d = new Date(dateStr + 'T00:00:00');
        return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
      }
      // Full ISO or anything Date() can parse
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  return (
    <div className="space-y-6" data-testid="csv-import-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Manrope'] text-2xl sm:text-3xl font-extrabold tracking-tight text-[#2B3674]">CSV Import</h1>
          <p className="text-sm text-[#A3AED0]">
            Import production data from carrier CSV files
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="import" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Import
          </TabsTrigger>
          <TabsTrigger value="exceptions" className="flex items-center gap-2">
            <FileWarning className="h-4 w-4" />
            Exceptions
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Import Tab — 5-step Wizard */}
        <TabsContent value="import" className="space-y-6 mt-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2" data-testid="wizard-stepper">
            {WIZARD_STEPS.map((step, idx) => {
              const StepIcon = step.icon;
              const isActive = wizardStep === step.id;
              const isComplete = wizardStep > step.id;
              return (
                <div key={step.id} className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-2">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                        isActive
                          ? 'bg-[#23ACD5] text-white ring-4 ring-[#23ACD5]/20'
                          : isComplete
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-200 text-[#A3AED0]'
                      }`}
                      data-testid={`wizard-step-${step.id}`}
                    >
                      {isComplete ? <CheckCircle2 className="h-4 w-4" /> : <StepIcon className="h-4 w-4" />}
                    </div>
                    <span
                      className={`text-sm font-medium hidden sm:inline ${
                        isActive ? 'text-[#2B3674]' : isComplete ? 'text-emerald-600' : 'text-[#A3AED0]'
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {idx < WIZARD_STEPS.length - 1 && (
                    <div className={`h-0.5 w-6 sm:w-10 ${wizardStep > step.id ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* ───────── Step 1: Upload ───────── */}
          {wizardStep === 1 && (
            <Card data-testid="wizard-step-1-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5 text-[#23ACD5]" />
                  Select Carrier & Upload CSV
                </CardTitle>
                <CardDescription>
                  Pick your carrier, then drop the CSV. Pay period is auto-detected from each policy's issued date. You'll map the columns to Atlas fields in the next step.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Carrier <span className="text-rose-500">*</span>
                    </label>
                    <Select value={selectedCarrier} onValueChange={setSelectedCarrier}>
                      <SelectTrigger data-testid="carrier-select">
                        <SelectValue placeholder="Select carrier" />
                      </SelectTrigger>
                      <SelectContent>
                        {carriers.map(carrier => (
                          <SelectItem key={carrier} value={carrier}>{carrier}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Pay Date <span className="text-[#A3AED0] font-normal">(optional)</span>
                    </label>
                    <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full md:w-72 justify-start text-left font-normal ${
                          !payDate ? 'text-[#A3AED0]' : ''
                        }`}
                        data-testid="pay-date-input"
                      >
                        <CalendarIcon className="h-4 w-4 mr-2 text-[#A3AED0]" />
                        {payDate
                          ? new Date(payDate + 'T00:00:00').toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric'
                            })
                          : 'Select pay date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={payDate ? new Date(payDate + 'T00:00:00') : undefined}
                        onSelect={(date) => {
                          if (!date) {
                            setPayDate('');
                            return;
                          }
                          // Store as YYYY-MM-DD (matches backend expectation)
                          const y = date.getFullYear();
                          const m = String(date.getMonth() + 1).padStart(2, '0');
                          const d = String(date.getDate()).padStart(2, '0');
                          setPayDate(`${y}-${m}-${d}`);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-[#A3AED0] mt-1">
                    Used as the issued/submitted date when a row has no date column or value.
                  </p>
                </div>
                </div>

                <div>
                  <label htmlFor="csv-file-input" className="block">
                    <div className="border-2 border-dashed border-[#E9EDF7] rounded-xl p-8 hover:border-[#23ACD5]/60 hover:bg-[#23ACD5]/5 transition-colors cursor-pointer text-center">
                      <Upload className="h-8 w-8 mx-auto text-[#A3AED0] mb-3" />
                      <p className="text-sm font-medium text-[#2B3674]">
                        Drop CSV file here or click to browse
                      </p>
                      <p className="text-xs text-[#A3AED0] mt-1">
                        Supports .csv files — any column order, we'll match it next.
                      </p>
                    </div>
                  </label>
                  <input
                    id="csv-file-input"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="csv-file-input"
                  />
                </div>

                {file && (
                  <div className="flex items-center gap-2 text-sm text-[#2B3674]/80 bg-[#F4F7FE] p-3 rounded-lg">
                    <FileSpreadsheet className="h-4 w-4 text-[#23ACD5]" />
                    <span className="font-medium">{file.name}</span>
                    <span className="text-[#A3AED0]">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                )}

                <div className="flex items-center justify-end pt-3 border-t">
                  <Button
                    onClick={handleParseHeaders}
                    disabled={!file || !selectedCarrier || parsing}
                    className="bg-[#23ACD5] hover:bg-[#1a9bc4]"
                    data-testid="wizard-next-1-btn"
                  >
                    {parsing ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Parsing…</>
                    ) : (
                      <>Next: Map Columns <ArrowRight className="h-4 w-4 ml-2" /></>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ───────── Step 2: Map Columns ───────── */}
          {wizardStep === 2 && parsedHeaders && (() => {
            // Defensive: filter out blank/null CSV column names. shadcn's
            // <SelectItem value=""> throws and crashes the whole tree, which
            // produced the "white screen" bug for CSVs with trailing commas.
            const safeColumns = (parsedHeaders.csv_columns || []).filter(c => typeof c === 'string' && c.trim().length > 0);
            return (
            <Card data-testid="wizard-step-2-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-[#23ACD5]" />
                  Map CSV Columns → Atlas Fields
                </CardTitle>
                <CardDescription>
                  We've auto-matched what we can. Confirm or adjust the mapping below. Required fields are marked with <span className="text-rose-500">*</span>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <Alert>
                  <FileSpreadsheet className="h-4 w-4" />
                  <AlertDescription>
                    <span className="font-medium">{parsedHeaders.filename}</span> — {parsedHeaders.total_rows} rows, {safeColumns.length} columns detected.
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {REQUIRED_COLUMNS.concat(OPTIONAL_COLUMNS).map(atlasField => {
                    const isRequired = REQUIRED_COLUMNS.includes(atlasField);
                    const currentValue = columnMapping[atlasField] || NONE_SENTINEL;
                    return (
                      <div key={atlasField} className="space-y-1.5">
                        <label className="block text-sm font-medium">
                          {ATLAS_FIELD_LABELS[atlasField]}{' '}
                          {isRequired ? (
                            <span className="text-rose-500">*</span>
                          ) : (
                            <span className="text-xs text-[#A3AED0] font-normal">(optional)</span>
                          )}
                        </label>
                        <Select
                          value={currentValue}
                          onValueChange={(val) => {
                            setColumnMapping(prev => {
                              const next = { ...prev };
                              if (val === NONE_SENTINEL) {
                                delete next[atlasField];
                              } else {
                                next[atlasField] = val;
                              }
                              return next;
                            });
                          }}
                        >
                          <SelectTrigger data-testid={`mapping-select-${atlasField}`}>
                            <SelectValue placeholder="— Not mapped —" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE_SENTINEL} className="text-[#A3AED0]">
                              — Not mapped —
                            </SelectItem>
                            {safeColumns.map(col => (
                              <SelectItem key={col} value={col}>
                                {col}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>

                {/* Sample rows preview */}
                {parsedHeaders.sample_rows?.length > 0 && (
                  <div className="pt-2">
                    <h3 className="text-sm font-medium mb-2 text-[#2B3674]">
                      Sample Rows (first {parsedHeaders.sample_rows.length})
                    </h3>
                    <div className="overflow-x-auto rounded-lg border bg-[#F4F7FE]/50">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {safeColumns.map(col => (
                              <TableHead key={col} className="text-xs">{col}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {parsedHeaders.sample_rows.map((row, idx) => (
                            <TableRow key={`sample-${idx}`}>
                              {safeColumns.map(col => (
                                <TableCell key={col} className="text-xs font-mono">
                                  {row[col] ?? '-'}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setWizardStep(1)}
                    data-testid="wizard-back-2-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                  <Button
                    onClick={handleValidateMapping}
                    disabled={validating || REQUIRED_COLUMNS.some(f => !columnMapping[f])}
                    className="bg-[#23ACD5] hover:bg-[#1a9bc4]"
                    data-testid="wizard-next-2-btn"
                  >
                    {validating ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Validating…</>
                    ) : (
                      <>Next: Preview <ArrowRight className="h-4 w-4 ml-2" /></>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
            );
          })()}

          {/* ───────── Step 3: Preview ───────── */}
          {wizardStep === 3 && validationResult && (
            <Card data-testid="wizard-step-3-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-[#23ACD5]" />
                  Review Before Importing
                </CardTitle>
                <CardDescription>
                  Double-check the matched records and exceptions below, then confirm to write to Atlas.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-[#F4F7FE] rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-[#2B3674]">
                      {validationResult.total_rows}
                    </div>
                    <div className="text-sm text-[#A3AED0]">Total Rows</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {validationResult.new_rows_count ?? validationResult.matched_rows}
                    </div>
                    <div className="text-sm text-green-600">New Records</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {validationResult.update_rows_count ?? 0}
                    </div>
                    <div className="text-sm text-blue-600">Updates</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">
                      {validationResult.exception_rows}
                    </div>
                    <div className="text-sm text-amber-600">Exceptions</div>
                  </div>
                </div>

                {/* Matched Preview */}
                {validationResult.preview_data?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Matched Records Preview (first 20)
                    </h3>
                    <div className="overflow-x-auto rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Row</TableHead>
                            <TableHead>Action</TableHead>
                            <TableHead>Agent (CSV)</TableHead>
                            <TableHead>Matched To</TableHead>
                            <TableHead>Insured</TableHead>
                            <TableHead>Policy #</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Submitted</TableHead>
                            <TableHead className="text-right">Annual Premium</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {validationResult.preview_data.map((row, idx) => (
                            <TableRow key={`preview-${row.row_number}-${row.policy_number}-${idx}`}>
                              <TableCell>{row.row_number}</TableCell>
                              <TableCell>
                                {row.is_update ? (
                                  <Badge className="bg-blue-500 hover:bg-blue-600 text-white border-blue-500">
                                    Update
                                  </Badge>
                                ) : (
                                  <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-500">
                                    New
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>{row.agent_name}</TableCell>
                              <TableCell className="text-green-600 font-medium">
                                {row.matched_agent_name}
                              </TableCell>
                              <TableCell>{row.insured_name || '-'}</TableCell>
                              <TableCell className="font-mono text-xs">{row.policy_number}</TableCell>
                              <TableCell>
                                <Badge variant={row.is_issued ? 'default' : 'secondary'}>
                                  {row.is_issued ? 'Issued' : 'Submitted'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs">{formatDate(row.submitted_date)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.annual_premium)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Exceptions Preview */}
                {validationResult.exceptions_preview?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Exceptions Preview (first 20)
                    </h3>
                    <div className="overflow-x-auto rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Row</TableHead>
                            <TableHead>Agent</TableHead>
                            <TableHead>Insured</TableHead>
                            <TableHead>Policy #</TableHead>
                            <TableHead>Reason</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {validationResult.exceptions_preview.map((row, idx) => (
                            <TableRow key={`exc-${row.row_number}-${row.policy_number}-${idx}`}>
                              <TableCell>{row.row_number}</TableCell>
                              <TableCell>{row.agent_name || '-'}</TableCell>
                              <TableCell>{row.insured_name || '-'}</TableCell>
                              <TableCell className="font-mono text-xs">{row.policy_number || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-amber-600 border-amber-300">
                                  {row.reason}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setWizardStep(2)}
                    data-testid="wizard-back-3-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Mapping
                  </Button>
                  <Button
                    onClick={handleConfirmImport}
                    disabled={importing || !(validationResult.matched_rows > 0)}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="confirm-import-btn"
                  >
                    {importing ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing…</>
                    ) : (
                      <><CheckCircle2 className="h-4 w-4 mr-2" /> Confirm & Import {validationResult.matched_rows} Records</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ───────── Step 4: Processing ───────── */}
          {wizardStep === 4 && (
            <Card data-testid="wizard-step-4-card">
              <CardContent className="py-16 text-center space-y-4">
                <Loader2 className="h-12 w-12 animate-spin text-[#23ACD5] mx-auto" />
                <div>
                  <p className="text-lg font-semibold text-[#2B3674]">Importing your data…</p>
                  <p className="text-sm text-[#A3AED0] mt-1">
                    Writing records to Atlas. This usually takes just a few seconds.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ───────── Step 5: Summary ───────── */}
          {wizardStep === 5 && importResult && (
            <Card data-testid="wizard-step-5-card" className="border-emerald-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-600">
                  <PartyPopper className="h-5 w-5" />
                  Import Complete!
                </CardTitle>
                <CardDescription>
                  Your data is now live across Atlas — agent portals, team rollups, and dashboards.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-emerald-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-600">
                      {importResult.records_inserted || 0}
                    </div>
                    <div className="text-sm text-emerald-600">New Records</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {importResult.records_updated || 0}
                    </div>
                    <div className="text-sm text-blue-600">Updated</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {importResult.issued_policies || 0}
                    </div>
                    <div className="text-sm text-green-600">Issued</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-amber-600">
                      {importResult.exceptions_logged || 0}
                    </div>
                    <div className="text-sm text-amber-600">Exceptions</div>
                  </div>
                </div>

                {importResult.exceptions_logged > 0 && (
                  <Alert>
                    <FileWarning className="h-4 w-4" />
                    <AlertDescription>
                      {importResult.exceptions_logged} row{importResult.exceptions_logged === 1 ? '' : 's'} couldn't be matched. Visit the <strong>Exceptions</strong> tab to assign them manually.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex items-center justify-between pt-3 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setActiveTab('history');
                      resetWizard();
                    }}
                  >
                    <History className="h-4 w-4 mr-2" /> View Import History
                  </Button>
                  <Button
                    onClick={resetWizard}
                    className="bg-[#23ACD5] hover:bg-[#1a9bc4]"
                    data-testid="wizard-restart-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" /> Import Another File
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Exceptions Tab */}
        <TabsContent value="exceptions" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileWarning className="h-5 w-5 text-amber-500" />
                    Import Exceptions
                  </CardTitle>
                  <CardDescription>
                    Review and resolve unmatched records from CSV imports
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchExceptions}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${exceptionsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {exceptionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#A3AED0]" />
                </div>
              ) : exceptions.length === 0 ? (
                <div className="text-center py-8 text-[#A3AED0]">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500/50" />
                  <p>No unresolved exceptions</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Agent Name</TableHead>
                        <TableHead>Insured</TableHead>
                        <TableHead>Carrier</TableHead>
                        <TableHead>Policy #</TableHead>
                        <TableHead className="text-right">AP</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Assign To</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {exceptions.map((exc) => (
                        <TableRow key={exc.id}>
                          <TableCell className="text-xs">{formatDate(exc.created_at)}</TableCell>
                          <TableCell>{exc.agent_name || '-'}</TableCell>
                          <TableCell>{exc.insured_name || '-'}</TableCell>
                          <TableCell>{exc.carrier}</TableCell>
                          <TableCell className="font-mono text-xs">{exc.policy_number || '-'}</TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            <div>{formatCurrency(exc.annual_premium || 0)}</div>
                            {exc.orphan_production_id ? (
                              <div className="text-[10px] text-emerald-600 font-medium mt-0.5">
                                In agency total
                              </div>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                              {exc.reason}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Select 
                              value={selectedAgentForRetry[exc.id] || ''} 
                              onValueChange={(val) => setSelectedAgentForRetry(prev => ({...prev, [exc.id]: val}))}
                            >
                              <SelectTrigger className="w-[150px] h-8 text-xs">
                                <SelectValue placeholder="Select agent" />
                              </SelectTrigger>
                              <SelectContent>
                                {agents.map(agent => (
                                  <SelectItem key={agent.id} value={agent.id} className="text-xs">
                                    {agent.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleRetryException(exc.id)}
                                disabled={!selectedAgentForRetry[exc.id]}
                              >
                                <UserCheck className="h-3.5 w-3.5" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-7 px-2 text-[#2B3674]/80 hover:text-[#2B3674]"
                                onClick={() => handleResolveException(exc.id)}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteException(exc.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Import History
                  </CardTitle>
                  <CardDescription>
                    Recent CSV imports and their results
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchHistory}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${historyLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#A3AED0]" />
                </div>
              ) : importHistory.length === 0 ? (
                <div className="text-center py-8 text-[#A3AED0]">
                  <History className="h-12 w-12 mx-auto mb-3 text-[#A3AED0]" />
                  <p>No import history yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Filename</TableHead>
                        <TableHead>Carrier</TableHead>
                        <TableHead>Total Rows</TableHead>
                        <TableHead>Imported</TableHead>
                        <TableHead>Exceptions</TableHead>
                        <TableHead>Imported By</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importHistory.map((imp) => {
                        const isDeleted = imp.status === 'deleted';
                        return (
                          <TableRow key={imp.id} className={isDeleted ? 'opacity-60' : ''}>
                            <TableCell className="text-xs">{formatDate(imp.completed_at || imp.created_at)}</TableCell>
                            <TableCell className="font-mono text-xs">{imp.filename}</TableCell>
                            <TableCell>{imp.carrier}</TableCell>
                            <TableCell>{imp.total_rows}</TableCell>
                            <TableCell>
                              <Badge variant="default" className="bg-green-100 text-green-700">
                                {imp.records_inserted || 0}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {imp.exceptions_logged > 0 ? (
                                <Badge variant="outline" className="text-amber-600 border-amber-300">
                                  {imp.exceptions_logged}
                                </Badge>
                              ) : (
                                <span className="text-[#A3AED0]">0</span>
                              )}
                            </TableCell>
                            <TableCell>{imp.created_by_name}</TableCell>
                            <TableCell>
                              {isDeleted ? (
                                <Badge variant="outline" className="text-rose-600 border-rose-300">
                                  Reversed
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                                  Active
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {isDeleted ? (
                                <span className="text-[11px] text-[#A3AED0]" title={`Reversed on ${formatDate(imp.deleted_at)}`}>
                                  — {imp.records_deleted || 0} deleted
                                </span>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeleteTarget(imp)}
                                  data-testid={`csv-delete-btn-${imp.id}`}
                                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                >
                                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                                  Delete
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* DANGER ZONE — admin-only bulk purge */}
          <Card className="mt-6 border-rose-300 bg-rose-50/40">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-rose-600" />
                <div>
                  <CardTitle className="text-rose-700">Danger Zone</CardTitle>
                  <CardDescription>
                    Permanently wipe <strong>every</strong> production record company-wide. Use this to reset a freshly-deployed environment or to clear bad seed data.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                variant="destructive"
                onClick={() => setPurgeConfirm(true)}
                data-testid="purge-all-production-btn"
                className="bg-rose-600 hover:bg-rose-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Purge All Production Records
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete-Import confirmation dialog */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open && !deletingImport) setDeleteTarget(null); }}
      >
        <AlertDialogContent data-testid="csv-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-500" />
              Reverse this CSV import?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <div>
                  This will permanently delete{' '}
                  <strong>{deleteTarget?.records_inserted || 0}</strong>{' '}
                  production record{(deleteTarget?.records_inserted || 0) === 1 ? '' : 's'}{' '}
                  that came from <span className="font-mono text-xs bg-[#E9EDF7] px-1.5 py-0.5 rounded">{deleteTarget?.filename}</span>.
                </div>
                <div className="text-[#A3AED0]">
                  Agent portals, team rollups, production dashboards, and carrier
                  breakdowns will all update immediately. Any exceptions logged
                  for this import will also be removed.
                </div>
                {(deleteTarget?.records_updated || 0) > 0 && (
                  <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800">
                    <strong>Note:</strong> This import also UPDATED {deleteTarget.records_updated} pre-existing record(s). Those will NOT be reverted (original values weren't snapshotted). You may want to correct them manually via the Production page's admin editor.
                  </div>
                )}
                <div className="font-medium text-rose-600">
                  This action cannot be undone.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingImport}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteImport}
              disabled={deletingImport}
              data-testid="csv-delete-confirm-btn"
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
            >
              {deletingImport ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reversing…</>
              ) : (
                <><Trash2 className="h-4 w-4 mr-2" /> Reverse Import</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Purge-All confirmation dialog */}
      <AlertDialog
        open={purgeConfirm}
        onOpenChange={(open) => { if (!open && !purging) { setPurgeConfirm(false); setPurgeTyped(''); } }}
      >
        <AlertDialogContent data-testid="purge-all-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" />
              Purge all production records?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <div>
                  This will permanently delete <strong>every</strong> production record across the entire company, plus all CSV-import exceptions.
                </div>
                <div className="text-[#A3AED0]">
                  Agent portals, team rollups, carrier breakdowns, and every dashboard will immediately show <strong>$0</strong>.
                </div>
                <div className="font-medium text-rose-600">
                  This cannot be undone.
                </div>
                <div className="pt-2">
                  <label className="block text-xs font-medium text-[#2B3674] mb-1">
                    Type <span className="font-mono bg-rose-100 px-1.5 py-0.5 rounded text-rose-700">PURGE</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={purgeTyped}
                    onChange={(e) => setPurgeTyped(e.target.value)}
                    placeholder="PURGE"
                    data-testid="purge-confirm-input"
                    className="w-full text-sm px-3 py-2 rounded-md border border-[#E9EDF7] bg-white focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={purging}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmPurgeAll}
              disabled={purging || purgeTyped !== 'PURGE'}
              data-testid="purge-all-confirm-btn"
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {purging ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Purging…</>
              ) : (
                <><Trash2 className="h-4 w-4 mr-2" /> Purge Everything</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
